"use client";

import { supabase } from "@/lib/supabase";
import type { DbWidget } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

// Columns the list consumers (home search dialog, widgets panel, project
// attach dialog) actually render — deliberately excludes `html_content`,
// which is only needed when opening a single widget (/widgets/[id]).
const WIDGET_LIST_COLUMNS = "id, user_id, team_id, name, description, created_at, sharing";
const WIDGET_LIST_LIMIT = 200;

export type WidgetListItem = Pick<
  DbWidget,
  "id" | "user_id" | "team_id" | "name" | "description" | "created_at" | "sharing"
> & { project?: { id: string; name: string; canManage?: boolean } | null };

export type UseWidgetsOptions = {
  /** Skip the list fetch — for consumers that only need the mutation helpers. */
  skipFetch?: boolean;
  /** Include project widgets and their visible project names in the library. */
  includeProjects?: boolean;
};

export type UseWidgetsReturn = {
  widgets: WidgetListItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createWidget: (params: {
    name: string;
    description?: string | null;
    htmlContent: string;
    teamId: string;
    sourceChatId?: string | null;
    originProjectId?: string | null;
  }) => Promise<string | null>;
  updateWidget: (
    widgetId: string,
    fields: Partial<Pick<DbWidget, "name" | "description" | "sharing" | "team_id" | "registers_access_granted">>
  ) => Promise<boolean>;
  deleteWidget: (widgetId: string) => Promise<boolean>;
};

export function useWidgets(options?: UseWidgetsOptions): UseWidgetsReturn {
  const includeProjects = options?.includeProjects ?? false;
  const [error, setError] = useState<string | null>(null);
  const skipFetch = options?.skipFetch ?? false;
  const [widgets, setWidgets] = useState<WidgetListItem[]>([]);
  const [loading, setLoading] = useState(!skipFetch);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (skipFetch) return;

    let cancelled = false;

    void (async () => {
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;

      if (cancelled) return;
      if (!user) {
        setWidgets([]);
        setLoading(false);
        return;
      }

      setError(null);
      try {
        const all: WidgetListItem[] = [];
        // Read every page rather than silently truncating the library at 200.
        for (let offset = 0; ; offset += WIDGET_LIST_LIMIT) {
          const { data, error: fetchError } = await supabase
            .from("widgets")
            .select(WIDGET_LIST_COLUMNS)
            .order("created_at", { ascending: false })
            .order("id")
            .range(offset, offset + WIDGET_LIST_LIMIT - 1);
          if (cancelled) return;
          if (fetchError) throw fetchError;
          const batch = (data ?? []) as WidgetListItem[];
          if (!batch.length) break;
          const { data: links, error: linkError } = await supabase
            .from("project_resources")
            .select("resource_id, project_id, projects(id, name, owner_id, project_members(user_id, role))")
            .eq("resource_type", "widget")
            .in("resource_id", batch.map((widget) => widget.id));
          if (cancelled) return;
          if (linkError) throw linkError;
          const projectByWidget = new Map(
            ((links ?? []) as unknown as Array<{
              resource_id: string;
              project_id: string;
              projects: { id: string; name: string; owner_id: string; project_members: Array<{ user_id: string; role: string }> } | null;
            }>).map((link) => [link.resource_id, {
              id: link.project_id,
              name: link.projects?.name ?? "Project",
              canManage: link.projects?.owner_id === user.id || Boolean(link.projects?.project_members.some((member) => member.user_id === user.id && member.role === "can_edit")),
            }])
          );
          all.push(...batch
            .filter((widget) => includeProjects || !projectByWidget.has(widget.id))
            .map((widget) => ({ ...widget, project: projectByWidget.get(widget.id) ?? null })));
          if (batch.length < WIDGET_LIST_LIMIT) break;
        }
        setWidgets(all);
      } catch (fetchError) {
        if (cancelled) return;
        console.error("[useWidgets] fetch:", fetchError);
        setWidgets([]);
        setError("Failed to load widgets. Please try again.");
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshKey, skipFetch, includeProjects]);

  const createWidget = useCallback(
    async ({
      name,
      description,
      htmlContent,
      teamId,
      sourceChatId,
      originProjectId,
    }: {
      name: string;
      description?: string | null;
      htmlContent: string;
      teamId: string;
      sourceChatId?: string | null;
      originProjectId?: string | null;
    }): Promise<string | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return null;

      const { data, error } = await supabase
        .from("widgets")
        .insert({
          user_id: user.id,
          team_id: teamId,
          name,
          description: description ?? null,
          html_content: htmlContent,
          sharing: "private",
          source_chat_id: sourceChatId ?? null,
          origin_project_id: originProjectId ?? null,
        })
        .select("id")
        .single();

      if (error) {
        console.error("[useWidgets] create:", error);
        return null;
      }

      refresh();
      return (data as { id: string } | null)?.id ?? null;
    },
    [refresh]
  );

  const updateWidget = useCallback(
    async (
      widgetId: string,
      fields: Partial<Pick<DbWidget, "name" | "description" | "sharing" | "team_id" | "registers_access_granted">>
    ): Promise<boolean> => {
      const { error } = await supabase
        .from("widgets")
        .update(fields)
        .eq("id", widgetId);

      if (error) {
        console.error("[useWidgets] update:", error);
        return false;
      }

      refresh();
      return true;
    },
    [refresh]
  );

  const deleteWidget = useCallback(
    async (widgetId: string): Promise<boolean> => {
      const { error } = await supabase
        .from("widgets")
        .delete()
        .eq("id", widgetId);

      if (error) {
        console.error("[useWidgets] delete:", error);
        return false;
      }

      refresh();
      return true;
    },
    [refresh]
  );

  return { widgets, loading, error, refresh, createWidget, updateWidget, deleteWidget };
}
